import { useCallback, useMemo } from 'react';

import { useCycleSettings } from './useCycleSettings';
import { useLocalStorage } from './useLocalStorage';

import { generateSeedLogs, predictCycle, type DayLog } from '@/lib/cycle';

const STORAGE_KEY = 'calmcycle:logs-v1';

// Seeded once per session so first-time users see a populated calendar.
const SESSION_SEED = generateSeedLogs();

export function useCycleLogs() {
  const [rawLogs, setLogs] = useLocalStorage<DayLog[]>(STORAGE_KEY, SESSION_SEED);
  const { settings } = useCycleSettings();

  // Guard against corrupted/partial stored values.
  const logs = Array.isArray(rawLogs) ? rawLogs : SESSION_SEED;

  const byDate = useMemo(() => new Map(logs.map((l) => [l.date, l])), [logs]);

  const upsertLog = useCallback(
    (log: DayLog) => {
      setLogs((prev) => {
        const rest = prev.filter((l) => l.date !== log.date);
        return [...rest, log].sort((a, b) => a.date.localeCompare(b.date));
      });
    },
    [setLogs],
  );

  // Manual cycle settings (last period start / cycle length) override the
  // dates auto-detected from the logs.
  const prediction = useMemo(
    () =>
      predictCycle(logs, new Date(), {
        lastPeriodStart: settings.lastPeriodStart,
        cycleLength: settings.cycleLength,
      }),
    [logs, settings.lastPeriodStart, settings.cycleLength],
  );

  return { logs, byDate, upsertLog, prediction };
}
